import React, { useState, useEffect } from 'react'
import {
  Box,
  Text,
  Button,
  ThemeContextExtend,
  IcoMoon,
  TextInput,
  Loader,
  useResponsiveWrapperContext,
  ResponsiveBox,
  MapIcon,
} from '@usap/uicomponents'
import FormTheme from './config/form-theme'
import CovidMessage from '../common/CovidMessage'

export default function ShipOrderOptions({
  shipNotifyForm,
  shipDetails = {},
  zipCode,
  cityName,
  zipCodeHandler,
  isLoading,
  zipcodeError,
  serpFlag = false,
  getDeliveryDate = () => {},
  index = 0,
  covidCookie
}) {
  const { isDesktop } = useResponsiveWrapperContext()

  const [zip, setZip] = useState('')
  const [error, setError] = useState(false)
  const [dates, setDates] = useState({})
  const mobileWidth = __BROWSER__ ? window.innerWidth : '320px'

  const validate = () => {
    return zipCode !== zip && /^[0-9]{5}(?:-[0-9]{4})?$/.test(zip)
  }
  const updateForm = (event) => {
    const { value } = event.target
    setZip(value)
  }

  // eslint-disable-next-line no-undef
  const showMessage = __BROWSER__ && window.getCookieRegex(covidCookie) === '1'

  const submitHandler = () => {
    const resp = validate()
    if (resp) {
      setError(false)
      if (serpFlag) {
        return zipCodeHandler(zip, true).then((res) => {
          if (res) return getDeliveryDate(index).then(setDates)
          return setError(true)
        })
      }
      return zipCodeHandler(zip)
    }
    return setError(true)
  }

  useEffect(() => {
    if (serpFlag) getDeliveryDate(index).then(setDates)
  }, [])

  const deliveryDates =
    (Object.keys(dates).length
      ? Object.values(dates)[0].deliveryDates
      : !serpFlag && shipDetails && shipDetails.deliveryDates) || []

  return (
    <ThemeContextExtend value={FormTheme}>
      {isLoading && <Loader />}
      <ResponsiveBox
        pad="medium"
        fxWidth={{
          small: mobileWidth,
          medium: '600px',
          large: shipNotifyForm ? '400px' : '580px'
        }}
        fxHeight={{ small: 'auto', other: '100%' }}
        style={{
          display: 'block',
          overflow: 'auto',
          'overflow-scrolling': 'touch',
          '-webkit-overflow-scrolling': 'touch'
        }}
      >
        {showMessage && <CovidMessage borderColor={'#4A90E2'} />}
        <Text
          color="dark-2"
          size="medium"
          weight={700}
          margin={{ vertical: 'small' }}
        >
          Ship Orders to
        </Text>

        {cityName && zipCode && (
          <Box direction="row" margin={{ vertical: 'xsmall' }}>
            <IcoMoon size="30px" icon={MapIcon} color="dark-2" />
            <Text
              size="medium"
              color="dark-2"
              alignSelf="center"
              weight={500}
              style={{ textTransform: 'capitalize' }}
            >
              {`${cityName} ${zipCode}`}
            </Text>
          </Box>
        )}

        <Box margin={{ vertical: 'small' }}>
          <Text size="small" color="dark-2" weight="bold">
            {`Zip Code`}
          </Text>
        </Box>

        <Box
          direction={!shipNotifyForm ? 'row' : 'column'}
          margin={{ bottom: 'small' }}
        >
          <Box>
            <TextInput
              id="zipCode"
              name="zipCode"
              type="text"
              size="small"
              style={{
                borderRadius: !shipNotifyForm ? '6px 0px 0px 6px' : '6px'
              }}
              onChange={updateForm}
              defaultValue={zipCode}
              onBlur={updateForm}
              onKeyUp={(event) => {
                if (event.keyCode === 13) submitHandler()
              }}
            />
          </Box>

          <Box>
            {shipNotifyForm ? (
              <Box width="100%" margin={{ top: 'small' }}>
                <Button
                  background="#2e4787"
                  id="submit"
                  form="submitForm"
                  type="submit"
                  forcedColor="dark-2"
                  hoverBackground="#2e4787"
                  custom={true}
                  hideBorder={true}
                  onClick={submitHandler}
                  label={
                    <Text size="small" color="white">
                      Apply
                    </Text>
                  }
                />
              </Box>
            ) : (
              <Button
                background="#2e4787"
                id="submit"
                form="submitForm"
                type="submit"
                forcedColor="dark-2"
                hoverBackground="#2e4787"
                custom={true}
                hideBorder={true}
                style={{
                  fontSize: '14px',
                  borderRadius: '0px 6px 6px 0px',
                  boxShadow: 'none'
                }}
                onClick={submitHandler}
                disabled={!zip || zip === zipCode}
                label={
                  <Text size="small" color="white">
                    Enter
                  </Text>
                }
              />
            )}
          </Box>
        </Box>

        <Box margin={{ bottom: 'small' }}>
          <Text size="12px" color="red">
            {(error || zipcodeError) && `Please enter a valid U.S ZIP Code`}
          </Text>
        </Box>

        {!shipNotifyForm && deliveryDates.length > 0 ? (
          <ResponsiveBox
            border="all"
            margin={{ vertical: 'small' }}
            fxAlign={{ large: 'start', other: 'center' }}
          >
            <ResponsiveBox
              fxDirection={{ large: 'row', other: 'column' }}
              background="#eef4fc"
              width="100%"
              style={{
                textAlign: isDesktop ? 'left' : 'center'
              }}
            >
              <ResponsiveBox
                fxWidth={{ large: '50%', other: '100%' }}
                pad="small"
                fxBorder={{ large: { side: 'right' } }}
              >
                <Text color="dark-2" size="small" weight="bold">
                  Shipping Method
                </Text>

                {!isDesktop && (
                  <Text color="dark-2" size="12px" weight={400}>
                    Available at Checkout
                  </Text>
                )}
              </ResponsiveBox>

              {isDesktop && (
                <Box width="50%" pad="small">
                  <Text color="dark-2" size="small" weight="bold">
                    Delivery Date
                  </Text>
                </Box>
              )}
            </ResponsiveBox>

            {deliveryDates.map((element, dateIndex) => (
              <ResponsiveBox
                key={dateIndex + `ship-date-details`}
                width="100%"
                fxDirection={{ large: 'row', other: 'column' }}
                fxBorder={{
                  large: { size: '0px' },
                  other:
                    dateIndex !== 0
                      ? {
                          side: 'top',
                          color: '#CCCACA',
                          size: 'xsmall'
                        }
                      : { size: '0px' }
                }}
                fxPad={{ large: '0px', other: '8px' }}
                style={{
                  textAlign: isDesktop ? 'left' : 'center'
                }}
              >
                <ResponsiveBox
                  fxWidth={{ large: '50%', other: '100%' }}
                  fxPad={{ large: 'small' }}
                  fxBorder={{
                    large: {
                      side: 'right'
                    }
                  }}
                >
                  <Text color="dark-2" size="small">
                    {element.label}
                  </Text>
                </ResponsiveBox>

                <ResponsiveBox
                  fxWidth={{ large: '50%', other: '100%' }}
                  fxPad={{ large: 'small' }}
                >
                  {isDesktop ? (
                    <Text color="dark-2" size="small">
                      {element.formattedDate}
                    </Text>
                  ) : (
                    <Text color="dark-2" size="small" weight="bold">
                      {element.formattedDate}
                    </Text>
                  )}
                </ResponsiveBox>
              </ResponsiveBox>
            ))}
          </ResponsiveBox>
        ) : (
          ''
        )}
        <Box>
          <Text size="xsmall" color="dark-2">
            {shipNotifyForm
              ? `*Shipping options and estimates may vary for different locations`
              : `*Shipping calculator is not available for Alaska, Hawaii and other countries.`}
          </Text>
          <Text size="xsmall" color="dark-2">
            **All available shipping options are displayed here and can be
            selected at checkout.
          </Text>
        </Box>
      </ResponsiveBox>
    </ThemeContextExtend>
  )
}
